import type {
  AggregatedReportFolder,
  AggregatedReportListItem,
} from '@/entities/report';

export type ReportListRow =
  | { id: 'parent'; kind: 'parent' }
  | {
      id: `folder:${string}`;
      kind: 'folder';
      folder: AggregatedReportFolder;
    }
  | {
      id: `report:${string}`;
      kind: 'report';
      report: AggregatedReportListItem;
    };

export const createFolderRow = (
  folder: AggregatedReportFolder,
): ReportListRow => ({
  id: `folder:${folder.alias}`,
  kind: 'folder',
  folder,
});

export const createReportRow = (
  report: AggregatedReportListItem,
): ReportListRow => ({
  id: `report:${report.aggregated_report_id}`,
  kind: 'report',
  report,
});

export const filterReportFolders = (
  folders: AggregatedReportFolder[],
  searchText: string,
) => {
  const normalizedSearch = searchText.trim().toLocaleLowerCase();
  if (!normalizedSearch) return folders;

  return folders.filter((folder) =>
    folder.name.toLocaleLowerCase().includes(normalizedSearch),
  );
};

export const calculateCombinedListPagination = ({
  pageIndex,
  pageSize,
  leadingItemCount,
}: {
  pageIndex: number;
  pageSize: number;
  leadingItemCount: number;
}) => {
  const pageStart = pageIndex * pageSize;
  const pageEnd = pageStart + pageSize;
  const leadingStart = Math.min(pageStart, leadingItemCount);
  const leadingEnd = Math.min(pageEnd, leadingItemCount);
  const leadingCountOnPage = Math.max(0, leadingEnd - leadingStart);

  return {
    leadingStart,
    leadingEnd,
    leadingCountOnPage,
    reportOffset: Math.max(0, pageStart - leadingItemCount),
    reportLimit: pageSize - leadingCountOnPage,
  };
};

export const createReportListRows = ({
  folders,
  reports,
  includeParentRow = false,
}: {
  folders: AggregatedReportFolder[];
  reports: AggregatedReportListItem[];
  includeParentRow?: boolean;
}): ReportListRow[] => {
  const parentRows: ReportListRow[] = includeParentRow
    ? [{ id: 'parent', kind: 'parent' }]
    : [];

  return [
    ...parentRows,
    ...folders.map(createFolderRow),
    ...reports.map(createReportRow),
  ];
};
